/**
 * File drag-and-drop and file-input safety for every screen. DOM-free apart
 * from the EventTarget it is given, so it can be tested without a browser.
 */

export function hasDraggedFiles(event: Event): boolean {
  const types = (event as Partial<DragEvent>).dataTransfer?.types;
  return types ? Array.from(types).includes('Files') : false;
}

export interface FileDropGuardOptions {
  /** True while the current screen takes dropped files (its own handlers read them). */
  readonly accepts: () => boolean;
  /** A file was dropped on a screen that does not take files. */
  readonly onRefused: () => void;
}

/**
 * Keeps the browser from opening a dropped file in the tab, on every screen:
 * that would replace the page (report, name cleanup) with the raw chat text.
 * Install it once for the page; returns a function that removes it.
 */
export function guardFileDrops(target: EventTarget, options: FileDropGuardOptions): () => void {
  const onDrag = (event: Event) => {
    if (hasDraggedFiles(event)) event.preventDefault();
  };
  const onDrop = (event: Event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    if (!options.accepts()) options.onRefused();
  };
  target.addEventListener('dragenter', onDrag);
  target.addEventListener('dragover', onDrag);
  target.addEventListener('drop', onDrop);
  return () => {
    target.removeEventListener('dragenter', onDrag);
    target.removeEventListener('dragover', onDrag);
    target.removeEventListener('drop', onDrop);
  };
}

/** The parts of <input type="file"> that watchFileInput uses. */
export type FileInputLike = Pick<HTMLInputElement, 'files' | 'value' | 'addEventListener' | 'removeEventListener'>;

/**
 * Passes files picked in a file input to `onFiles` until the returned function
 * is called. A native file dialog can outlive the screen that opened it; a pick
 * made after that is dropped instead of starting a parse from another screen.
 */
export function watchFileInput(input: FileInputLike, onFiles: (files: File[]) => void): () => void {
  let active = true;
  const onChange = () => {
    const files = active && input.files ? Array.from(input.files) : [];
    input.value = '';
    if (files.length > 0) onFiles(files);
  };
  input.addEventListener('change', onChange);
  return () => {
    active = false;
    input.removeEventListener('change', onChange);
  };
}
